"use client";

import { updatePrescription } from "@/api/saleor/services";
import { hasArrayElements } from "@/apiUtils/commonUtils";
import { lensConfigurationSteps } from "@/apiUtils/constants";
import { endPageTransition, startPageTransition } from "@/apiUtils/pageLoadingUtils";
import CheckIcon from "@/assets/icons/checked.svg?react";
import { ConfiguratorStepProps, PrescriptionData } from "@/common/lib/types";
import Button from "@/components/ui/button/Button";
import { getItemMeta, getPreviousStep, getSkuMeta } from "@/utils/lensConfigurator";
import { useMemo, useState } from "react";
import Navigation from "../../components/Navigation";
import { useConfiguratorContext } from "../../context/Provider";
import UploadPrescription from "../../phaseOne/prescription/upload/UploadPrescription";
import { lensTypes } from "../../resource/config";
import SendItLater from "./SendItLater";
import TakeOrUpload from "./TakeOrUpload";
import UseSavedPrescription from "./UseSavedPrescription";
import SummaryReview from "../../summaryReview/SummaryReview";
import React from "react";
import { getAttributeValue } from "@/apiUtils/productUtils";
import PairEyewearTopModal from "@/components/pairEyewear/PairEyewearTopModal";
import { getLangFromLocale } from "@/utils/common";

interface IProps extends ConfiguratorStepProps {
  stepOptions: any;
  isSendLater?: boolean;
  cta?: React.ReactNode;
  hideSendItLater?: boolean;
  onCloseLensConfigurator?: () => void;
}

const ReviewAndPrescription = ({
  cta,
  locale,
  isSendLater,
  currencyCode,
  translations,
  handleAddToCart,
  handleJumpToStep,
  hideSendItLater,
  onCloseLensConfigurator,
}: IProps) => {
  const [sendLater, setSendLater] = useState(false);
  const [ctaLoading, setCtaLoading] = useState(false);
  const [uploadedPrescription, setUploadedPrescription] = useState<PrescriptionData | null>(null);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  const lang = getLangFromLocale(locale);

  const currentStep = lensConfigurationSteps.REVIEW;
  const {
    sku,
    steps,
    loading,
    filters,
    summary,
    lensType,
    productInfo,
    skippedSteps,
    fromReviewStep,
    updateFromReviewStep,
    updateFilters,
  } = useConfiguratorContext();

  const isPairEyewear = productInfo?.brand === "Pair Eyewear";
  const isBundle = productInfo?.isBundle;

  const model = getAttributeValue(productInfo?.attributes, "model") as string;

  const topsContent = productInfo?.pairEyewearTopsContent;

  const handleToggleSendLater = () => {
    setSendLater((prev) => !prev);
  };

  const handleToggleUploadMenu = () => {
    updateFromReviewStep((state) => !state);
    setSendLater(false);
  };

  const handleUpload = async (step: lensConfigurationSteps, data: PrescriptionData) => {
    setUploadedPrescription(data);
    handleToggleUploadMenu();
  };
  const handleClose = () => {
    window.location.reload();
    setIsOpen(false);
  };

  const handleEdit = async (data?: PrescriptionData) => {
    if (!data) return;
    const formData = new FormData();
    const updated = await updatePrescription(data.id, formData, data.title);
    if (updated) setUploadedPrescription(data);
  };

  const handleDelete = async (id?: string) => {
    if (!id) return;
    setUploadedPrescription(null);
    delete filters.PRESCRIPTION;
    updateFilters({ ...filters });
  };

  const handleBack = async () => {
    if (typeof handleJumpToStep !== "function") return;
    startPageTransition(false);
    await handleJumpToStep(getPreviousStep(currentStep, steps, skippedSteps));
    endPageTransition();
  };

  const handleLocalAddToCart = async () => {
    startPageTransition(false);
    setCtaLoading(true);
    const meta = getItemMeta(summary, sendLater, prescription);
    const skuIdMeta = getSkuMeta(sku, prescription);
    if (typeof handleAddToCart === "function" && productInfo?.variantId) {
      const result = await handleAddToCart(productInfo.variantId, skuIdMeta, prescription?.id, meta);
      if (typeof onCloseLensConfigurator === "function") {
        onCloseLensConfigurator();
      }
    }
    setCtaLoading(false);
    endPageTransition();
  };

  const handleAddToCartAndSelectTops = async () => {
    startPageTransition(false);
    setCtaLoading(true);
    const meta = getItemMeta(summary, sendLater, prescription);
    const skuIdMeta = getSkuMeta(sku, prescription);
    if (typeof handleAddToCart === "function" && productInfo?.variantId) {
      await handleAddToCart(productInfo.variantId, skuIdMeta, prescription?.id, meta);
    }
    setCtaLoading(false);

    setIsOpen(true);
    endPageTransition();
  };

  const prescription: PrescriptionData = uploadedPrescription || filters.PRESCRIPTION;
  const hasPrescriptionSelected = !!prescription;
  const prescriptionRequired = lensType === lensTypes.SINGLE_VISION;

  const cards: JSX.Element[] = useMemo(() => {
    const data = [];
    if (hasPrescriptionSelected) {
      if (prescription?.newPrescription || isSendLater) {
        data.push(
          <TakeOrUpload
            handleEdit={handleEdit}
            hideDelete={isSendLater}
            prescription={prescription}
            translations={translations}
            handleDelete={handleDelete}
            handleToggleUploadMenu={() => { }}
          />,
        );
        if (!hideSendItLater) {
          data.push(
            <SendItLater
              disabled={loading}
              status={sendLater}
              translations={translations}
              onToggle={handleToggleSendLater}
            />,
          );
        }
      } else if (prescription) {
        data.push(
          <UseSavedPrescription
            prescription={prescription}
            translations={translations}
          />,
        );
      }
    }

    if (prescriptionRequired && !hasPrescriptionSelected) {
      data.push(
        <TakeOrUpload
          translations={translations}
          handleToggleUploadMenu={handleToggleUploadMenu}
        />,
      );
      data.push(
        <SendItLater
          disabled={false}
          status={sendLater}
          translations={translations}
          onToggle={handleToggleSendLater}
        />,
      );
    }
    return data;
  }, [
    hasPrescriptionSelected,
    prescription,
    prescriptionRequired,
    sendLater,
    loading,
    translations,
    isSendLater,
    hideSendItLater,
  ]);

  if (fromReviewStep)
    return (
      <UploadPrescription
        locale={locale}
        currencyCode={currencyCode}
        translations={translations}
        handleStepData={handleUpload}
        toggle={handleToggleUploadMenu}
        customTitle={translations?.lensConfigurator?.lens_config_review_section_title}
      />
    );

  return (
    <>
      <div>
        <SummaryReview
          locale={locale}
          className="mb-6 mt-0"
          translations={translations}
          currencyCode={currencyCode}
        />
        {isSendLater ? (
          <>
            <div className="flex items-center gap-2">
              <p>{translations?.lensConfigurator?.lens_config_send_later_review_title}</p>
              <CheckIcon />
            </div>
            <p className="mb-6 text-[0.75rem] text-tertiary-6 max-sm:px-4">
              <span className="mr-1 font-semibold">
                {translations?.lensConfigurator?.lens_config_send_later_review_subtitle_bold_text}
              </span>
              <span>{translations?.lensConfigurator?.lens_config_send_later_review_subtitle}</span>
            </p>
          </>
        ) : (
          <div>
            <Navigation disabled={loading} onGoBack={handleBack} className="hidden md:flex max-sm:px-4">
              {translations?.lensConfigurator?.lens_config_review_section_title}
            </Navigation>
            <p className="mb-6 text-[0.75rem] text-tertiary-6 max-sm:px-4">
              <span className="mr-1 font-semibold">
                {translations?.lensConfigurator?.lens_config_review_section_subtitle_bold_text}
              </span>
              <span>{translations?.lensConfigurator?.lens_config_review_section_subtitle}</span>
            </p>
          </div>
        )}

        <div className="flex w-full flex-col gap-4 lg:w-[36rem] max-sm:px-4">
          {hasArrayElements(cards) &&
            cards.map((card, index) => <React.Fragment key={index}>{card}</React.Fragment>)}
        </div>
        <div className="flex justify-center lg:justify-start max-sm:px-4">
          {cta ? (
            cta
          ) : isPairEyewear && !isBundle ? (
            <div className="mt-9 flex flex-col-reverse sm:flex-row gap-3 w-full max-w-[36rem]">
              <Button
                variant="contained"
                onClick={handleLocalAddToCart}
                className="flex-1 whitespace-nowrap px-3 py-2 sm:px-4 sm:py-2.5 md:px-5 md:py-3 border border-black bg-white text-black hover:bg-gray-50 text-xs sm:text-sm max-sm:variant-link max-sm:border-none max-sm:bg-transparent max-sm:underline max-sm:py-0"
                disabled={
                  (prescriptionRequired && !hasPrescriptionSelected && !sendLater) || ctaLoading
                }
              >
                  {translations?.lensConfigurator?.lens_config_review_section_add_to_cart_button_title}
              </Button>
              <Button
                onClick={handleAddToCartAndSelectTops}
                className="flex-1 whitespace-nowrap px-3 py-2 sm:px-4 sm:py-2.5 md:px-5 md:py-3 bg-black text-white hover:bg-gray-800 text-xs sm:text-sm"
                disabled={
                  (prescriptionRequired && !hasPrescriptionSelected && !sendLater) || ctaLoading
                }
              >
                {translations?.pdp?.pdp_add_to_cart_and_select_tops_button ??
                  "Add to cart and select tops"}
              </Button>
            </div>
          ) : (
            <Button
              variant="contained"
              onClick={handleLocalAddToCart}
              className="mt-9 w-full max-w-[36rem] px-6 py-4 max-sm:mt-6"
              disabled={
                (prescriptionRequired && !hasPrescriptionSelected && !sendLater) || ctaLoading
              }
            >
              {translations?.lensConfigurator?.lens_config_review_section_add_to_cart_button_title}
            </Button>
          )}
        </div>
      </div>
      {isPairEyewear && (
        <PairEyewearTopModal
          isOpen={isOpen}
          onClose={handleClose}
          pairEyewearTopsContent={topsContent ?? null}
          translations={translations}
          locale={locale}
          missingTopModel={[{ model }]}
          variantIdsToExcludeFromAddedCount={productInfo?.variantId ? [productInfo.variantId] : undefined}
        />
      )}
    </>
  );
};

export default ReviewAndPrescription;
